import { Injectable } from '@angular/core';

import { StringDictionary, isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { GridModel } from '@frontend/sports/grid/core/feature/model';
import { keyBy } from 'lodash-es';

export interface EventCollapsedState {
    until: Date;
    collapsed: boolean;
    collapsedChildren: string[];
}

export interface GroupCollapsedState {
    collapsed: boolean;
}

export interface ColumnActiveState {
    index: number;
    group: string;
    extended: boolean;
}

export interface GridStoreState {
    groups: number[];
    events: StringDictionary<EventCollapsedState>;
    columns: ColumnActiveState[];
}

@Injectable({ providedIn: 'root' })
export class GridStoreService {
    private state = new Map<string, GridStoreState>();

    set(grid: GridModel, group?: number): void {
        const actualState = this.get(grid.id);

        const events = this.getEventsState(grid);
        const columns = this.getColumnState(grid);
        const groups = actualState ? actualState.groups : [];

        if (group) {
            const groupIndex = groups.indexOf(group);

            if (groupIndex === -1) {
                groups.push(group);
            } else {
                groups.splice(groupIndex, 1);
            }
        }

        if (columns.length > 0 || groups.length > 0 || Object.keys(events).length > 0) {
            this.state.set(grid.id, { events, columns, groups });
        } else {
            this.state.delete(grid.id);
        }
    }

    get(id: string): GridStoreState | undefined {
        const expiry = Date.now();

        for (const state of this.state.values()) {
            // eslint-disable-next-line guard-for-in
            for (const eventKey in state.events) {
                const event = state.events[eventKey];

                if (event.until.getTime() < expiry) {
                    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                    delete state.events[eventKey];
                }
            }
        }

        return this.state.get(id);
    }

    private getColumnState(grid: GridModel): ColumnActiveState[] {
        return grid.columns
            .map((column, index) => {
                const activeIndex = column.groups.findIndex((group) => group.active);

                if (activeIndex === -1 || activeIndex === index) {
                    return;
                }
                const active = column.groups[activeIndex];

                return {
                    index,
                    group: active.id,
                    extended: active.extended,
                };
            })
            .filter(isDefined);
    }

    private getEventsState(grid: GridModel): StringDictionary<EventCollapsedState> {
        const state: StringDictionary<EventCollapsedState> = {};
        const until = Date.now();

        const events = grid.groups.flatMap((group) => group.events);
        const eventsMap = keyBy(events, (event) => event.id);

        // eslint-disable-next-line guard-for-in
        for (const id in grid.collapsed.events) {
            const event = eventsMap[id];
            const eventState = grid.collapsed.events[id];

            if (!event || event.cutOffDate.getTime() <= until) {
                continue;
            }

            if (eventState.collapsed || eventState.collapsedChildren.length > 0) {
                state[id] = {
                    until: event.cutOffDate,
                    collapsed: eventState.collapsed,
                    collapsedChildren: eventState.collapsedChildren,
                };
            }
        }

        return state;
    }
}
